import Loader from "Components/Loading/Loader/Loader";
import { TaskTemplate } from "Models/Tasks/TaskTemplate";
import * as React from "react";
import { connect } from "react-redux";
import { Action, Dispatch } from "redux";
import ServiceProvider from "ServiceProvider/ServiceProvider";
import Types from "ServiceProvider/Types";
import { IApplicationState } from "Store/Store";
import { ITaskTemplateAction } from "Store/TaskTemplates/TaskTemplateAction";
import { ITaskTemplateState } from "Store/TaskTemplates/TaskTemplateState";
import i18n from "Translations/i18n";
import { clamp, updateOrder } from "Utils/OrderUtils";
import TaskTemplateListCard from "./TaskTemplateListCard/TaskTemplateListCard";
import styles from "./TaskTemplateListView.scss";

const taskTemplateAction: ITaskTemplateAction = ServiceProvider.get<ITaskTemplateAction>(Types.ITaskTemplateAction);

const mapDispatchToProps = (dispatch: Dispatch) => ({
    updatePositionIndexAsync: (taskTemplates: TaskTemplate[]) =>
        dispatch(taskTemplateAction.updatePositionIndexAsync(taskTemplates)),
    clearTaskTemplatesAsync: () =>
        dispatch(taskTemplateAction.clearTaskTemplatesAsync()),
    clearArrangeTaskTemplatesAsync: () =>
        dispatch(taskTemplateAction.clearArrangeTaskTemplatesAsync()),
});

const mapStateToProps = (state: IApplicationState) => ({
    taskTemplateState: state.taskTemplateState
});

interface IProps {
    filteredAreaIds: number[];
    taskTemplateState: ITaskTemplateState;
    updatePositionIndexAsync: (taskTemplates: TaskTemplate[]) => Action;
    clearTaskTemplatesAsync: () => Action;
    clearArrangeTaskTemplatesAsync: () => Action;
}

interface IState {
    arrangeTaskTemplates?: TaskTemplate[];
    lastPressed?: TaskTemplate;
    isPressed: boolean;
    mouseY: number;
    topDeltaY: number;
    oldIndex: number;
}

export class TaskTemplateListView extends React.Component<IProps, IState> {
    private readonly ITEM_HEIGHT = 70;
    private readonly refList = React.createRef<HTMLDivElement>();
    private mousePageY: number;

    constructor(props: IProps) {
        super(props);

        this.state = {
            arrangeTaskTemplates: props.taskTemplateState.arrangeTaskTemplates,
            isPressed: false,
            mouseY: 0,
            topDeltaY: 0,
            oldIndex: 0
        };

        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.update = this.update.bind(this);
        this.startAutoScroll = this.startAutoScroll.bind(this);
        this.calcIndexByPosition = this.calcIndexByPosition.bind(this);
        this.calcPositionByIndex = this.calcPositionByIndex.bind(this);
        this.isFiltered = this.isFiltered.bind(this);
    }

    public handleTouchStart(taskTemplate: TaskTemplate, index: number, pressY: number, e: React.TouchEvent<HTMLDivElement>) {
        this.setState({
            topDeltaY: e.touches[0].pageY - pressY,
            mouseY: pressY,
            isPressed: true,
            lastPressed: taskTemplate,
            oldIndex: index
        });

        window.addEventListener("touchmove", this.handleTouchMove);
        window.addEventListener("touchend", this.handleTouchEnd);
        this.startAutoScroll();
    }

    public handleTouchMove(e: any) {
        e.preventDefault();
        this.handleMouseMove(e.touches[0]);
    }

    public handleMouseDown(taskTemplate: TaskTemplate, index: number, pressY: number, e: React.MouseEvent) {
        this.setState({
            topDeltaY: e.pageY - pressY,
            mouseY: pressY,
            isPressed: true,
            lastPressed: taskTemplate,
            oldIndex: index
        });

        window.addEventListener("mousemove", this.handleMouseMove);
        window.addEventListener("mouseup", this.handleMouseUp);
        this.startAutoScroll();
    }

    public handleMouseMove(e: { pageY: number, screenY: number }) {
        this.mousePageY = e.screenY;
        const { topDeltaY, isPressed, arrangeTaskTemplates, lastPressed } = this.state;

        if (isPressed && lastPressed !== undefined) {
            const curIndex = arrangeTaskTemplates!.indexOf(lastPressed);

            const mouseY = e.pageY - topDeltaY;
            (this.refList.current!.children[curIndex] as HTMLDivElement).style.top = `${mouseY}px`;

            const newIndex = clamp(
                this.calcIndexByPosition(mouseY),
                0,
                arrangeTaskTemplates!.length - 1
            );

            if (newIndex !== curIndex) {
                this.setState({ arrangeTaskTemplates: updateOrder(arrangeTaskTemplates!, curIndex, newIndex), mouseY });
            }
        }
    }

    private startAutoScroll() {
        window.setInterval(() => {
            if (this.state.isPressed) {
                if (this.mousePageY > window.innerHeight - this.ITEM_HEIGHT) {
                    window.scrollBy(0, 2);
                }
                else if (this.mousePageY < this.ITEM_HEIGHT + 60) {
                    window.scrollBy(0, -2);
                }
            }
        }, 1);
    }

    private update() {
        if (this.state.lastPressed) {
            const taskTemplates = this.state.arrangeTaskTemplates!;
            const curIndex = taskTemplates.indexOf(this.state.lastPressed);
            this.refList.current!.children[curIndex].style.top = `${this.calcPositionByIndex(curIndex)}px`;

            const updatedTemplates: TaskTemplate[] = [];
            for (let i = 0; i < taskTemplates.length; ++i) {
                if (taskTemplates[i].positionIndex !== i) {
                    updatedTemplates.push({ ...taskTemplates[i], positionIndex: i });
                    taskTemplates[i].positionIndex = i;
                }
            }

            if (updatedTemplates.length > 0) {
                this.props.updatePositionIndexAsync(updatedTemplates);
                this.props.clearTaskTemplatesAsync();
            }

            this.setState({ isPressed: false });
        }
    }

    public handleMouseUp() {
        this.update();

        window.removeEventListener("mouseup", this.handleMouseUp);
        window.removeEventListener("mousemove", this.handleMouseMove);
    }

    public handleTouchEnd() {
        this.update();

        window.removeEventListener("touchmove", this.handleTouchMove);
        window.removeEventListener("touchend", this.handleTouchEnd);
    }

    public componentWillUnmount() {
        this.props.clearArrangeTaskTemplatesAsync();
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        const partialState: Partial<IState> = {};

        if ((!prevState.arrangeTaskTemplates || prevState.arrangeTaskTemplates.length === 0) && nextProps.taskTemplateState && nextProps.taskTemplateState.arrangeTaskTemplates !== prevState.arrangeTaskTemplates) {
            partialState.arrangeTaskTemplates = nextProps.taskTemplateState.arrangeTaskTemplates;
        }

        return partialState;
    }

    private calcPositionByIndex(index: number): number {
        let yPos = 0;
        for (let i = 0; i < index; ++i) {
            yPos += this.isFiltered(this.state.arrangeTaskTemplates![i]) ? 0 : this.ITEM_HEIGHT;
        }
        return yPos;
    }

    private calcIndexByPosition(y: number) {
        let yPos = 0;
        if (y <= 0) {
            for (let i = 0; i < this.state.arrangeTaskTemplates!.length; ++i) {
                if (!this.isFiltered(this.state.arrangeTaskTemplates![i])) {
                    return i;
                }
            }
            return 0;
        }

        for (let i = 0; i < this.state.arrangeTaskTemplates!.length; ++i) {
            yPos += this.isFiltered(this.state.arrangeTaskTemplates![i]) ? 0 : this.ITEM_HEIGHT;

            if (y < yPos) {
                return i;
            }
        }

        for (let i = this.state.arrangeTaskTemplates!.length - 1; i >= 0; --i) {
            if (!this.isFiltered(this.state.arrangeTaskTemplates![i])) {
                return i;
            }
        }
        return this.state.arrangeTaskTemplates!.length - 1;
    }

    private isFiltered(taskTemplate: TaskTemplate) {
        return this.props.filteredAreaIds.length && (!taskTemplate.area || this.props.filteredAreaIds.indexOf(taskTemplate.area.id) === -1);
    }

    public render() {
        const { arrangeTaskTemplates, lastPressed, isPressed, mouseY } = this.state;
        let yPos = 0;

        return (
            <div className={styles.taskTemplateListView}>
                {(arrangeTaskTemplates && arrangeTaskTemplates.length) ?
                    <div className={styles.list} ref={this.refList}>
                        {arrangeTaskTemplates.map((taskTemplate, i) => {
                            if (!this.isFiltered(taskTemplate)) {
                                const isActive = isPressed && lastPressed === taskTemplate;
                                const y = isActive ? mouseY : yPos;
                                yPos += this.ITEM_HEIGHT;

                                return (
                                    <TaskTemplateListCard
                                        key={taskTemplate.id}
                                        taskTemplate={taskTemplate}
                                        className={`${isActive && styles.active}`}
                                        style={{ top: `${y}px` }}
                                        onMouseDown={(e: any) => this.handleMouseDown(taskTemplate, i, y, e)}
                                        onTouchStart={(e: any) => this.handleTouchStart(taskTemplate, i, y, e)}
                                    />
                                );
                            }
                            else {
                                return <div key={taskTemplate.id} />;
                            }
                        })}
                    </div>
                    : (this.props.taskTemplateState.isArrangeLoading) ? (<Loader />) : (<span>{i18n.t("tasks.noTasksFound")}</span>)}
            </div>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(TaskTemplateListView);


// WEBPACK FOOTER //
// ./src/Components/Pages/TaskTemplateListOverview/TaskTemplateListView/TaskTemplateListView.tsx